import { ArrowUpCircle, Gamepad2, Upload, Wrench } from "lucide-react";
import { ExternalLinkButton } from "@/components/blocks/ExternalLinkButton";
import {
	ChouicHeroScene,
	CoupleQuizScene,
	MigrationTimeline,
	ReleaseFeedScene,
} from "@/components/blocks/scenes/ChouicScenes";
import { CardGrid, toCardItems } from "@/components/blocks/sections/CardGrid";
import { Cta } from "@/components/blocks/sections/Cta";
import { Facts } from "@/components/blocks/sections/Facts";
import { NumberedPoints } from "@/components/blocks/sections/NumberedPoints";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { Section } from "@/components/blocks/sections/Section";
import {
	displayH2,
	Overline,
	SectionHeading,
} from "@/components/blocks/sections/SectionHeading";
import { CaseBlock } from "@/components/blocks/work/CaseBlock";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/motion";
import { CHOUIC_URL } from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";

const CTA_ID = "contact";

const CHALLENGE_ICONS = {
	builds: Wrench,
	upgrades: ArrowUpCircle,
	stores: Upload,
	roadmap: Gamepad2,
};

export function ChouicCasePage() {
	const { dict } = useI18n();
	const page = dict.chouicCase;
	const client = page.client;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
				visual={<ChouicHeroScene />}
				footer={
					<div className="mx-auto w-full max-w-7xl px-6 pb-16 lg:px-10">
						<Facts items={page.facts} />
					</div>
				}
			>
				<ExternalLinkButton
					href={CHOUIC_URL}
					cta="chouic"
					placement="case_hero"
					variant="outline"
				>
					{page.siteLink}
				</ExternalLinkButton>
			</PageHero>

			<Section>
				<div className="grid items-center gap-14 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:gap-20">
					<Reveal className="space-y-6">
						<Overline index="01">{client.label}</Overline>
						<h2 className={displayH2}>{client.title}</h2>
						<p className="max-w-xl text-lg text-pretty text-muted-foreground">
							{client.body}
						</p>
						<p className="max-w-xl text-pretty text-muted-foreground">
							{client.team}
						</p>
						<div className="max-w-xl rounded-3xl bg-ch-ink p-6 text-white md:p-7">
							<div className="flex flex-wrap items-center justify-between gap-4">
								<img
									src="/clients/chouic-logo.webp"
									alt={client.logoAlt}
									width={693}
									height={240}
									loading="lazy"
									className="h-8 w-auto"
								/>
								<span className="font-mono text-[11px] tracking-[0.06em] text-white/65 uppercase">
									{client.tagline}
								</span>
							</div>
							<p className="mt-6 font-mono text-[11px] tracking-[0.06em] text-white/65 uppercase">
								{client.portfolioLabel}
							</p>
							<ul className="mt-3 flex flex-wrap gap-2">
								{Object.entries(client.portfolio).map(([key, name]) => (
									<li
										key={key}
										className="rounded-full border border-white/20 px-3 py-1 text-sm font-medium"
									>
										{name}
									</li>
								))}
							</ul>
						</div>
					</Reveal>
					<Reveal delay={0.15}>
						<CoupleQuizScene />
					</Reveal>
				</div>
			</Section>

			<Section tone="stone" containerClassName="space-y-12">
				<SectionHeading
					index="02"
					label={page.challenge.label}
					title={page.challenge.title}
					description={page.challenge.description}
				/>
				<CardGrid
					columns={4}
					items={toCardItems(page.challenge.items, { icons: CHALLENGE_ICONS })}
				/>
			</Section>

			<Section containerClassName="space-y-12">
				<SectionHeading
					index="03"
					label={page.intervention.label}
					title={page.intervention.title}
					description={page.intervention.description}
				/>
				<Reveal>
					<MigrationTimeline />
				</Reveal>
				<NumberedPoints items={page.intervention.steps} columns />
			</Section>

			<Section tone="night" containerClassName="space-y-14">
				<SectionHeading
					index="04"
					label={page.results.label}
					title={page.results.title}
					description={page.results.description}
				/>
				<RevealGroup
					as="dl"
					className="grid border-t sm:grid-cols-3 sm:divide-x sm:divide-border"
				>
					{Object.entries(page.results.stats).map(([key, stat]) => (
						<RevealItem
							key={key}
							className="flex flex-col-reverse gap-3 border-b py-8 sm:border-b-0 sm:px-8 sm:first:pl-0 sm:last:pr-0"
						>
							<dt className="max-w-[16rem] text-pretty text-muted-foreground">
								{stat.label}
							</dt>
							<dd className="font-mono text-[clamp(2.75rem,6vw,4.5rem)] leading-none font-semibold tracking-[-0.06em] text-signal-ink">
								{stat.value}
							</dd>
						</RevealItem>
					))}
				</RevealGroup>
				<div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-20">
					<NumberedPoints items={page.results.points} />
					<Reveal delay={0.1} className="flex justify-center lg:justify-end">
						<ReleaseFeedScene />
					</Reveal>
				</div>
			</Section>

			<Section>
				<CaseBlock index="05" title={page.next.title}>
					<p>{page.next.body}</p>
				</CaseBlock>
			</Section>

			<Cta
				id={CTA_ID}
				{...page.cta}
				need="expoMigration"
				placement="case_cta"
			/>
		</>
	);
}
